import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from '@tanstack/react-table';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
    FiUser, FiHash, FiCalendar, FiClock, FiTool, FiDollarSign,
    FiHome, FiLayers, FiCheckCircle, FiAlertCircle,
    FiX, FiChevronRight, FiGrid, FiList
} from "react-icons/fi";
import { GiWoodBeam } from "react-icons/gi";
import { BrickWall, Grid2X2 } from "lucide-react";

import AssignmentContextMenu from './AssignmentContextMenu';
import './ContextMenu.css';
import { searchJobs } from '../../../lib/supabase/assignments';
import { assignDesignerToJob } from '../../../lib/supabase/assignments';

const StickyToolbar = ({
    searchQuery,
    setSearchQuery,
    selectedQuoteTypes,
    setSelectedQuoteTypes,
    viewMode,
    setViewMode,
    columnVisibility,
    setColumnVisibility,
    isVisible,
    navbarHeight,
    expandedAssignments,
    filteredAssignments
}) => {
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowColumnDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            className={`fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-lg transition-all duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            style={{
                top: `${navbarHeight}px`,
                transform: isVisible ? 'translateY(0)' : `translateY(-100%)`
            }}
        >
            <div className="max-w-[1800px] mx-auto px-4 py-3">
                <div className="flex items-center gap-3">
                    {/* Compact Search */}
                    <div className="relative flex-1 min-w-[250px] max-w-[400px]">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search..."
                            className="w-full h-8 px-3 pl-8 pr-8 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <FiGrid className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Quote Type Filters */}
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, roof: !prev.roof }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.roof
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Roof"
                        >
                            <FiHome className="w-3 h-3" />
                            <span className="hidden sm:inline">Roof</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, wall: !prev.wall }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.wall
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Wall"
                        >
                            <BrickWall className="w-3 h-3" />
                            <span className="hidden sm:inline">Wall</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, floor: !prev.floor }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.floor
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Floor"
                        >
                            <Grid2X2 className="w-3 h-3" />
                            <span className="hidden sm:inline">Floor</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, timber: !prev.timber }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.timber
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Timber"
                        >
                            <GiWoodBeam className="w-3 h-3" />
                            <span className="hidden sm:inline">Timber</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, estimation: !prev.estimation }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.estimation
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Estimation"
                        >
                            <FiDollarSign className="w-3 h-3" />
                            <span className="hidden sm:inline">Est</span>
                        </button>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-gray-100 rounded-md border h-8">
                        <button
                            onClick={() => setViewMode('separated')}
                            className={`px-2 h-full text-xs font-medium rounded transition-colors ${viewMode === 'separated'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Separated
                        </button>
                        <button
                            onClick={() => setViewMode('combined')}
                            className={`px-2 h-full text-xs font-medium rounded transition-colors ${viewMode === 'combined'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Combined
                        </button>
                    </div>

                    {/* Column Visibility Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                            className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            <FiList className="w-3.5 h-3.5" />
                            Columns
                            <FiChevronRight className={`w-3 h-3 transition-transform ${showColumnDropdown ? 'rotate-90' : ''}`} />
                        </button>

                        {showColumnDropdown && (
                            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] max-h-96 overflow-y-auto">
                                <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-gray-700">Toggle Columns</span>
                                        <button
                                            onClick={() => {
                                                const allVisible = Object.values(columnVisibility).every(Boolean);
                                                const newVisibility = Object.keys(columnVisibility).reduce((acc, key) => {
                                                    acc[key] = !allVisible;
                                                    return acc;
                                                }, {});
                                                setColumnVisibility(newVisibility);
                                            }}
                                            className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                                        </button>
                                    </div>
                                </div>

                                <div className="p-2 space-y-1">
                                    {[
                                        { key: 'uniqueId', label: 'Job ID', icon: FiHash },
                                        { key: 'customerName', label: 'Customer', icon: FiUser },
                                        { key: 'jobType', label: 'Type', icon: FiTool },
                                        { key: 'mainStatus', label: 'Status', icon: FiClock },
                                        { key: 'quotes', label: 'Quotes', icon: FiLayers },
                                        { key: 'assignee', label: 'Assignee', icon: FiUser },
                                        { key: 'priority', label: 'Priority', icon: FiAlertCircle },
                                        { key: 'invoiced', label: 'Invoice', icon: FiDollarSign },
                                        { key: 'createdAt', label: 'Created', icon: FiCalendar },
                                        { key: 'etaDate', label: 'ETA Date', icon: FiCalendar },
                                    ].map(({ key, label, icon: Icon }) => (
                                        <label
                                            key={key}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${columnVisibility[key] ? 'bg-blue-50/50' : ''
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility[key] ?? true}
                                                onChange={(e) =>
                                                    setColumnVisibility(prev => ({
                                                        ...prev,
                                                        [key]: e.target.checked,
                                                    }))
                                                }
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <Icon className="w-3 h-3 text-gray-500" />
                                            <span className="text-xs text-gray-700">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Row Count - Right aligned */}
                    <div className="ml-auto">
                        <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 h-8 rounded-full inline-flex items-center">
                            {expandedAssignments.length} rows ({filteredAssignments.length} assignments)
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ColumnToggle = ({
    searchQuery,
    setSearchQuery,
    columnVisibility,
    setColumnVisibility,
    setColumnSizing,
    selectedQuoteTypes,
    setSelectedQuoteTypes
}) => (
    <div className="mb-4 space-y-3">
        {/* Search Bar */}
        <div className="relative">
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Job ID, Customer, Assignee, Status..."
                className="w-full px-4 py-2.5 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            <FiGrid className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            {searchQuery && (
                <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                    <FiX className="w-4 h-4" />
                </button>
            )}
        </div>

        {/* Column Toggle Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FiList className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700">Column Visibility</span>
                    </div>
                    <div className='flex flex-row gap-3'>
                        <button
                            onClick={() => {
                                const allVisible = Object.values(columnVisibility).every(Boolean);
                                const newVisibility = Object.keys(columnVisibility).reduce((acc, key) => {
                                    acc[key] = !allVisible;
                                    return acc;
                                }, {});
                                setColumnVisibility(newVisibility);
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                            {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                        </button>
                        <button
                            onClick={() => {
                                setColumnVisibility({
                                    uniqueId: true,
                                    customerName: true,
                                    jobType: true,
                                    mainStatus: true,
                                    quotes: true,
                                    assignee: true,
                                    priority: true,
                                    createdAt: true,
                                    invoiced: true,
                                    etaDate: true
                                });
                                setColumnSizing({});
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                            Reset Table
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                        { key: 'uniqueId', label: 'Job ID', icon: FiHash },
                        { key: 'customerName', label: 'Customer', icon: FiUser },
                        { key: 'jobType', label: 'Type', icon: FiTool },
                        { key: 'mainStatus', label: 'Status', icon: FiClock },
                        { key: 'quotes', label: 'Quotes', icon: FiLayers },
                        { key: 'assignee', label: 'Assignee', icon: FiUser },
                        { key: 'priority', label: 'Priority', icon: FiAlertCircle },
                        { key: 'invoiced', label: 'Invoice', icon: FiDollarSign },
                        { key: 'createdAt', label: 'Created', icon: FiCalendar },
                        { key: 'etaDate', label: 'ETA Date', icon: FiCalendar },
                    ].map(({ key, label, icon: Icon }) => (
                        <label
                            key={key}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-all cursor-pointer ${columnVisibility[key]
                                ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={columnVisibility[key] ?? true}
                                onChange={(e) =>
                                    setColumnVisibility(prev => ({
                                        ...prev,
                                        [key]: e.target.checked,
                                    }))
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                            />
                            <Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 font-medium">{label}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Quote Type Filters */}
            <div className="flex items-start gap-2 flex-col p-4 pt-0">
                <span className="text-xs font-medium text-gray-600">Filter by Quote Type:</span>
                <div className='flex flex-row gap-2 flex-wrap'>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, roof: !prev.roof }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.roof
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <FiHome className="w-3 h-3" />
                        Roof
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, wall: !prev.wall }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.wall
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <BrickWall className="w-3 h-3" />
                        Wall
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, floor: !prev.floor }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.floor
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <Grid2X2 className="w-3 h-3" />
                        Floor
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, timber: !prev.timber }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.timber
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <GiWoodBeam className="w-3 h-3" />
                        Timber
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, estimation: !prev.estimation }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.estimation
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <FiDollarSign className="w-3 h-3" />
                        Estimation
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes({ roof: true, wall: true, floor: true, timber: true, estimation: true })}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors ml-2"
                    >
                        Show All
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const JobEditorTable = ({
    assignments,
    userType,
    company,
    navigate,
    availableDesigners,
    onLoadMore,
    hasMore,
    isLoadingMore,
    isLoading = false,
    scheduleType,
    showOnlyMine,
    selectedUser,
    companyId
}) => {
    const [viewMode, setViewMode] = useState('separated');
    const [expandedAssignments, setExpandedAssignments] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [filteredAssignments, setFilteredAssignments] = useState(assignments);
    const [searchResults, setSearchResults] = useState([]);
    const [showStickyToolbar, setShowStickyToolbar] = useState(false);
    const [navbarHeight, setNavbarHeight] = useState(0);
    const columnToggleRef = useRef(null);

    const [selectedQuoteTypes, setSelectedQuoteTypes] = useState({
        roof: true,
        wall: true,
        floor: true,
        estimation: true,
        timber: true
    });

    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => clearTimeout(handler);
        }, [value, delay]);

        return debouncedValue;
    };

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const STORAGE_KEYS = {
        columnVisibility: 'jobEditor_columnVisibility',
        columnSizing: 'jobEditor_columnSizing'
    };

    const saveToStorage = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn('Failed to save to localStorage:', error);
        }
    };

    const loadFromStorage = (key, defaultValue) => {
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : defaultValue;
        } catch (error) {
            console.warn('Failed to load from localStorage:', error);
            return defaultValue;
        }
    };

    const [columnVisibility, setColumnVisibility] = useState(() =>
        loadFromStorage(STORAGE_KEYS.columnVisibility, {
            uniqueId: true,
            customerName: true,
            jobType: true,
            mainStatus: true,
            quotes: true,
            assignee: true,
            priority: true,
            createdAt: true,
            invoiced: true,
            etaDate: true
        })
    );

    const [columnSizing, setColumnSizing] = useState(() =>
        loadFromStorage(STORAGE_KEYS.columnSizing, {})
    );

    const expandAssignmentsByQuoteType = (assignments) => {
        const expanded = [];

        assignments.forEach(assignment => {
            if (viewMode === 'combined') {
                expanded.push({
                    ...assignment,
                    _displayId: assignment._id || assignment.id,
                    _originalId: assignment._id || assignment.id,
                    _quoteType: null,
                    _isCombined: true
                });
            } else {
                const requiredQuotes = [];

                if (assignment.quotes?.roof?.isRequired && selectedQuoteTypes.roof) {
                    requiredQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id || assignment.id}_roof`,
                        _originalId: assignment._id || assignment.id,
                        _quoteType: 'roof',
                        _quoteData: assignment.quotes.roof,
                        _isCombined: false
                    });
                }

                if (assignment.quotes?.wall?.isRequired && selectedQuoteTypes.wall) {
                    requiredQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id || assignment.id}_wall`,
                        _originalId: assignment._id || assignment.id,
                        _quoteType: 'wall',
                        _quoteData: assignment.quotes.wall,
                        _isCombined: false
                    });
                }

                if (assignment.quotes?.floor?.isRequired && selectedQuoteTypes.floor) {
                    requiredQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id || assignment.id}_floor`,
                        _originalId: assignment._id || assignment.id,
                        _quoteType: 'floor',
                        _quoteData: assignment.quotes.floor,
                        _isCombined: false
                    });
                }

                if (assignment.quotes?.timber?.isRequired && selectedQuoteTypes.timber) {
                    requiredQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id || assignment.id}_timber`,
                        _originalId: assignment._id || assignment.id,
                        _quoteType: 'timber',
                        _quoteData: assignment.quotes.timber,
                        _isCombined: false
                    });
                }

                const needsEstimation = (assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation') && selectedQuoteTypes.estimation;

                if (requiredQuotes.length === 0 && needsEstimation) {
                    expanded.push({
                        ...assignment,
                        _displayId: assignment._id || assignment.id,
                        _originalId: assignment._id || assignment.id,
                        _quoteType: null,
                        _isCombined: true
                    });
                } else {
                    expanded.push(...requiredQuotes);
                }
            }
        });

        return expanded;
    };

    const useInfiniteScroll = (callback, hasMore, isLoading) => {
        const [isFetching, setIsFetching] = useState(false);

        useEffect(() => {
            if (!isFetching) return;
            fetchMoreData();
        }, [isFetching]);

        const fetchMoreData = async () => {
            await callback();
            setIsFetching(false);
        };

        useEffect(() => {
            const handleScroll = () => {
                if (
                    window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight ||
                    isFetching ||
                    !hasMore ||
                    isLoading
                ) return;
                setIsFetching(true);
            };

            window.addEventListener('scroll', handleScroll);
            return () => window.removeEventListener('scroll', handleScroll);
        }, [isFetching, hasMore, isLoading]);

        return [isFetching, setIsFetching];
    };

    const [isFetching] = useInfiniteScroll(onLoadMore, hasMore, isLoadingMore);

    const shouldShowAssignment = (assignment) => {
        const hasRoof = assignment.quotes?.roof?.isRequired;
        const hasWall = assignment.quotes?.wall?.isRequired;
        const hasFloor = assignment.quotes?.floor?.isRequired;
        const hasTimber = assignment.quotes?.timber?.isRequired;
        const hasEstimation = assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation';

        const matchesFilter = (
            (hasRoof && selectedQuoteTypes.roof) ||
            (hasWall && selectedQuoteTypes.wall) ||
            (hasFloor && selectedQuoteTypes.floor) ||
            (hasTimber && selectedQuoteTypes.timber) ||
            (hasEstimation && selectedQuoteTypes.estimation)
        );

        return matchesFilter;
    };

    useEffect(() => {
        const detectNavbarHeight = () => {
            const navbar =
                document.querySelector('nav') ||
                document.querySelector('[role="navigation"]') ||
                document.querySelector('header') ||
                document.querySelector('.navbar') ||
                document.querySelector('#navbar');

            if (navbar) {
                const rect = navbar.getBoundingClientRect();
                setNavbarHeight(rect.height);
            } else {
                setNavbarHeight(0);
            }
        };

        const handleScroll = () => {
            if (columnToggleRef.current) {
                const rect = columnToggleRef.current.getBoundingClientRect();
                setShowStickyToolbar(rect.bottom < navbarHeight);
            }
        };

        detectNavbarHeight();
        window.addEventListener('resize', detectNavbarHeight);
        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('resize', detectNavbarHeight);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [navbarHeight]);

    // Filter assignments based on search
    useEffect(() => {
        if (!debouncedSearchQuery.trim()) {
            const filtered = assignments.filter(assignment => {
                return shouldShowAssignment(assignment);
            });
            setFilteredAssignments(filtered);
            setSearchResults([]);
            return;
        }

        const performSearch = async () => {
            setIsTableLoading(true);

            try {
                const filters = {
                    searchQuery: debouncedSearchQuery,
                    ...(showOnlyMine && { showOnlyMine: true }),
                    ...(selectedUser && { userId: selectedUser })
                };

                const serverResults = await searchJobs(companyId, filters);

                const loadedIds = new Set(assignments.map(a => a._id || a.id));
                const newResults = serverResults.filter(a => !loadedIds.has(a._id || a.id));

                const localMatches = assignments.filter(assignment => {
                    const query = debouncedSearchQuery.toLowerCase();
                    const matchesSearch = (
                        assignment.uniqueId?.toLowerCase().includes(query) ||
                        assignment.unique_id?.toLowerCase().includes(query) ||
                        (assignment._id || assignment.id)?.toLowerCase().includes(query) ||
                        assignment.customerName?.toLowerCase().includes(query) ||
                        assignment.customer_name?.toLowerCase().includes(query) ||
                        assignment.carpenterSupervisorName?.toLowerCase().includes(query) ||
                        assignment.carpenter_supervisor_name?.toLowerCase().includes(query) ||
                        assignment.jobType?.toLowerCase().includes(query) ||
                        assignment.job_type?.toLowerCase().includes(query) ||
                        assignment.mainStatus?.toLowerCase().includes(query) ||
                        assignment.main_status?.toLowerCase().includes(query) ||
                        assignment.estimator?.name?.toLowerCase().includes(query) ||
                        assignment.quotes?.roof?.designers?.some(d => d.name?.toLowerCase().includes(query)) ||
                        assignment.quotes?.wall?.designers?.some(d => d.name?.toLowerCase().includes(query)) ||
                        assignment.quotes?.floor?.designers?.some(d => d.name?.toLowerCase().includes(query))
                    );
                    return matchesSearch && shouldShowAssignment(assignment);
                });

                const filteredNewResults = newResults.filter(shouldShowAssignment);
                setSearchResults(serverResults);
                setFilteredAssignments([...localMatches, ...filteredNewResults]);
            } catch (error) {
                console.error('Search failed:', error);
                const query = debouncedSearchQuery.toLowerCase();
                const filtered = assignments.filter(assignment => {
                    const matchesSearch = (
                        assignment.uniqueId?.toLowerCase().includes(query) ||
                        assignment.unique_id?.toLowerCase().includes(query) ||
                        (assignment._id || assignment.id)?.toLowerCase().includes(query) ||
                        assignment.customerName?.toLowerCase().includes(query) ||
                        assignment.customer_name?.toLowerCase().includes(query) ||
                        assignment.carpenterSupervisorName?.toLowerCase().includes(query) ||
                        assignment.carpenter_supervisor_name?.toLowerCase().includes(query) ||
                        assignment.jobType?.toLowerCase().includes(query) ||
                        assignment.job_type?.toLowerCase().includes(query) ||
                        assignment.mainStatus?.toLowerCase().includes(query) ||
                        assignment.main_status?.toLowerCase().includes(query) ||
                        assignment.estimator?.name?.toLowerCase().includes(query) ||
                        assignment.quotes?.roof?.designers?.some(d => d.name?.toLowerCase().includes(query)) ||
                        assignment.quotes?.wall?.designers?.some(d => d.name?.toLowerCase().includes(query)) ||
                        assignment.quotes?.floor?.designers?.some(d => d.name?.toLowerCase().includes(query))
                    );
                    return matchesSearch && shouldShowAssignment(assignment);
                });
                setFilteredAssignments(filtered);
            } finally {
                setIsTableLoading(false);
            }
        };

        performSearch();
    }, [debouncedSearchQuery, assignments, scheduleType, showOnlyMine, selectedUser, companyId, selectedQuoteTypes]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.columnVisibility, columnVisibility);
    }, [columnVisibility]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.columnSizing, columnSizing);
    }, [columnSizing]);

    useEffect(() => {
        const expanded = expandAssignmentsByQuoteType(filteredAssignments);
        setExpandedAssignments(expanded);
    }, [filteredAssignments, viewMode, selectedQuoteTypes]);

    const handleOpenAssignment = (assignmentId) => {
        window.open(`/${company}/assignment/${assignmentId}`, '_blank');
    };

    const handleEditAssignment = (assignmentId) => {
        window.open(`/${company}/assignment/${assignmentId}/edit`, '_blank');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const getPriorityIndicator = (assignment) => {
        const isUrgent = assignment.jobInformation?.urgentQuote || assignment.jobInformation?.urgentDesign ||
            assignment.job_information?.urgent_quote || assignment.urgent_quote;
        if (isUrgent) return { color: 'bg-red-500', label: 'Urgent' };
        return { color: 'bg-gray-300', label: 'Normal' };
    };

    const handleAssignDesigner = async (assignmentId, quoteType, designerId, designerName) => {
        try {
            await assignDesignerToJob(assignmentId, quoteType, designerId);
            console.log(`Designer ${designerName} assigned to ${quoteType} for assignment ${assignmentId}`);
        } catch (error) {
            console.error('Failed to assign designer:', error);
        }
    };

    const columns = [
        {
            id: 'uniqueId',
            accessorKey: 'uniqueId',
            header: () => (
                <div className="flex items-center">
                    <FiHash className="w-3 h-3 mr-2 text-gray-500" />
                    Job ID
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                        {row.original.uniqueId || row.original.unique_id}
                        {row.original._quoteType && (
                            <span className="ml-2 text-xs font-medium text-gray-500">
                                ({row.original._quoteType.charAt(0).toUpperCase() + row.original._quoteType.slice(1)})
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        ID: {(row.original._id || row.original.id).slice(-9)}
                    </div>
                </div>
            ),
            size: 180,
            minSize: 120,
            maxSize: 300,
        },
        {
            id: 'customerName',
            accessorKey: 'customerName',
            header: () => (
                <div className="flex items-center">
                    <FiUser className="w-3 h-3 mr-2 text-gray-500" />
                    Customer
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-800 truncate">
                        {row.original.customerName || row.original.customer_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {row.original.carpenterSupervisorName || row.original.carpenter_supervisor_name || 'No supervisor'}
                    </div>
                </div>
            ),
            size: 200,
            minSize: 150,
            maxSize: 300,
        },
        {
            id: 'jobType',
            accessorKey: 'jobType',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiTool className="w-3 h-3 mr-1 text-gray-500" />
                    Type
                </div>
            ),
            cell: ({ row }) => {
                const jobType = row.original.jobType || row.original.job_type;
                return (
                    <div className="text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${jobType === 'Design' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                            jobType === 'Estimation' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                                'bg-emerald-100 text-emerald-800 border border-emerald-200'
                            }`}>
                            {jobType === 'Design' ? 'D' : jobType === 'Estimation' ? 'E' : 'D&E'}
                        </span>
                    </div>
                );
            },
            size: 100,
            minSize: 80,
            maxSize: 120,
        },
        {
            id: 'mainStatus',
            accessorKey: 'mainStatus',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiClock className="w-3 h-3 mr-1 text-gray-500" />
                    Status
                </div>
            ),
            cell: ({ row }) => {
                const status = row.original.mainStatus || row.original.main_status;
                const isTimber = row.original.quotes?.timber?.isRequired;

                const displayStatus = isTimber
                    ? (status === 'ready_for_delivery' ? 'ready for delivery' :
                        status === 'completed' ? 'Completed' : status?.replace('_', ' '))
                    : status?.replace('_', ' ');

                return (
                    <div className="text-center">
                        <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${status === 'completed' ? 'bg-green-100 text-green-700' :
                            status === 'ready_for_delivery' && isTimber ? 'bg-teal-100 text-teal-700' :
                                status?.includes('in_') ? 'bg-blue-100 text-blue-700' :
                                    status?.includes('pending') ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                            }`}>
                            {displayStatus}
                        </div>
                    </div>
                );
            },
            size: 140,
            minSize: 100,
            maxSize: 160,
        },
        {
            id: 'quotes',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiLayers className="w-3 h-3 mr-1 text-gray-500" />
                    Quotes
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    {row.original._isCombined ? (
                        row.original.quotes?.timber?.isRequired ? (
                            <div className="flex items-center gap-1">
                                <GiWoodBeam className="w-5 h-5 text-gray-400" title="Timber Job" />
                            </div>
                        ) : (
                            <QuotesHoverTooltip assignment={row.original} />
                        )
                    ) : (
                        <SingleQuoteDisplay
                            assignment={row.original}
                            quoteType={row.original._quoteType}
                        />
                    )}
                </div>
            ),
            size: 160,
            minSize: 120,
            maxSize: 180,
        },
        {
            id: 'assignee',
            header: () => (
                <div className="flex items-center">
                    <FiUser className="w-3 h-3 mr-2 text-gray-500" />
                    Assignee
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-700 truncate">
                        {row.original.estimator?.name ||
                            row.original.quotes?.roof?.designers?.[0]?.name ||
                            row.original.quotes?.wall?.designers?.[0]?.name ||
                            row.original.quotes?.floor?.designers?.[0]?.name ||
                            'Unassigned'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {row.original.estimator?.name ? 'Estimator' :
                            (row.original.quotes?.roof?.designers?.[0] || row.original.quotes?.wall?.designers?.[0] || row.original.quotes?.floor?.designers?.[0]) ? 'Designer' :
                                'No assignment'}
                    </div>
                </div>
            ),
            size: 160,
            minSize: 120,
            maxSize: 200,
        },
        {
            id: 'priority',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiAlertCircle className="w-3 h-3 mr-1 text-gray-500" />
                    Priority
                </div>
            ),
            cell: ({ row }) => {
                const priority = getPriorityIndicator(row.original);
                const urgentQuote = row.original.jobInformation?.urgentQuote || row.original.urgent_quote;
                const urgentDesign = row.original.jobInformation?.urgentDesign || row.original.urgent_design;
                return (
                    <div className="flex items-center justify-center gap-1">
                        <div className={`w-3 h-3 rounded-full ${priority.color} shadow-sm`} title={priority.label}></div>
                        <div className="flex gap-1">
                            {urgentQuote && (
                                <span className="px-1 py-0.5 text-xs bg-red-100 text-red-700 rounded border" title="Urgent Quote">
                                    UQ
                                </span>
                            )}
                            {urgentDesign && (
                                <span className="px-1 py-0.5 text-xs bg-orange-100 text-orange-700 rounded border" title="Urgent Design">
                                    UD
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
            size: 120,
            minSize: 100,
            maxSize: 140,
        },
        {
            id: 'invoiced',
            accessorKey: 'invoiced',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiDollarSign className="w-3 h-3 mr-1 text-gray-500" />
                    Invoice
                </div>
            ),
            cell: ({ row }) => {
                const invoiced = row.original.invoiced;
                return (
                    <div className="flex justify-center">
                        {invoiced ? (
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs font-medium text-green-700">Paid</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                <span className="text-xs font-medium text-gray-600">Pending</span>
                            </div>
                        )}
                    </div>
                );
            },
            size: 90,
            minSize: 70,
            maxSize: 100,
        },
        {
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiCalendar className="w-3 h-3 mr-1 text-gray-500" />
                    Created
                </div>
            ),
            cell: ({ row }) => {
                const date = new Date(row.original.createdAt || row.original.created_at);
                return (
                    <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">
                            {date.toLocaleDateString('en-AU')}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                );
            },
            size: 110,
            minSize: 90,
            maxSize: 130,
        },
        {
            id: 'etaDate',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiCalendar className="w-3 h-3 mr-1 text-gray-500" />
                    ETA Date
                </div>
            ),
            cell: ({ row }) => {
                const quotes = row.original.quotes || {};
                const etaDates = [
                    quotes.roof?.expectedFinishDate || quotes.roof?.expected_finish_date,
                    quotes.wall?.expectedFinishDate || quotes.wall?.expected_finish_date,
                    quotes.floor?.expectedFinishDate || quotes.floor?.expected_finish_date,
                    row.original.expectedEstimationFinishDate || row.original.expected_estimation_finish_date,
                    row.original.bookingDeliveryDate || row.original.booking_delivery_date
                ].filter(Boolean);

                const latestDate = etaDates.length > 0 ?
                    new Date(Math.max(...etaDates.map(date => new Date(date)))) : null;

                return (
                    <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">
                            {formatDate(latestDate)}
                        </div>
                        {latestDate && (
                            <div className="text-xs text-gray-500 mt-0.5">
                                {Math.ceil((new Date(latestDate) - new Date()) / (1000 * 60 * 60 * 24))} days
                            </div>
                        )}
                    </div>
                );
            },
            size: 120,
            minSize: 100,
            maxSize: 140,
        },
    ];

    const table = useReactTable({
        data: expandedAssignments,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        onColumnVisibilityChange: setColumnVisibility,
        onColumnSizingChange: setColumnSizing,
        defaultColumn: {
            minSize: 60,
            maxSize: 600,
        },
        state: {
            columnVisibility,
            columnSizing,
        },
    });

    const LoadingRow = () => (
        <tr>
            <td colSpan={columns.length} className="px-4 py-8 text-center">
                <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Loading more assignments...</span>
                </div>
            </td>
        </tr>
    );

    const QuotesHoverTooltip = ({ assignment }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 200;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;

                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getQuoteStatusColor = (quoteType) => {
            if (quoteType === 'estimation') {
                if (!assignment.estimator?.userId) return 'text-red-500';
                if (!assignment.estimator?.isCompleted) return 'text-yellow-500';
                return 'text-green-500';
            }

            const quote = assignment.quotes?.[quoteType];
            if (!quote?.isRequired) return 'text-gray-300';

            if (assignment.jobType?.includes('Design') || assignment.job_type?.includes('Design')) {
                switch (quote.designStatus || quote.design_status) {
                    case 'waiting_assignment': return 'text-red-500';
                    case 'in_progress': return 'text-yellow-500';
                    case 'completed': return 'text-green-500';
                    default: return 'text-gray-400';
                }
            }
            return 'text-blue-500';
        };

        const getEstimationStatus = () => {
            if (!assignment.estimator?.userId) return 'waiting_assignment';
            if (!assignment.estimator?.isCompleted && !assignment.estimator?.is_completed) return 'in_progress';
            return 'completed';
        };

        const estimationStatus = getEstimationStatus();

        const getQuoteIcon = (quoteType) => {
            switch (quoteType) {
                case 'roof': return <FiHome className="w-3 h-3" />;
                case 'wall': return <BrickWall className="w-3 h-3" />;
                case 'floor': return <Grid2X2 className="w-3 h-3" />;
                case 'timber': return <GiWoodBeam className="w-3 h-3" />;
                default: return <FiLayers className="w-3 h-3" />;
            }
        };

        const getStatusDot = (status) => {
            switch (status) {
                case 'completed': return 'bg-green-500';
                case 'in_progress': return 'bg-yellow-500';
                case 'waiting_assignment': return 'bg-red-500';
                default: return 'bg-gray-400';
            }
        };

        const getRequiredQuotes = () => {
            const quotes = [];
            if (assignment.quotes?.roof?.isRequired || assignment.quotes?.roof?.is_required) {
                quotes.push({ type: 'roof', ...assignment.quotes.roof });
            }
            if (assignment.quotes?.wall?.isRequired || assignment.quotes?.wall?.is_required) {
                quotes.push({ type: 'wall', ...assignment.quotes.wall });
            }
            if (assignment.quotes?.floor?.isRequired || assignment.quotes?.floor?.is_required) {
                quotes.push({ type: 'floor', ...assignment.quotes.floor });
            }
            if (assignment.quotes?.timber?.isRequired || assignment.quotes?.timber?.is_required) {
                quotes.push({ type: 'timber', ...assignment.quotes.timber, designStatus: 'not_required' });
            }
            return quotes;
        };

        const requiredQuotes = getRequiredQuotes();
        const needsEstimation = (assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation') ||
            (assignment.job_type === 'Estimation' || assignment.job_type === 'Design & Estimation');

        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 256 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'
                        }`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 256 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-xs">Status</h4>

                    {requiredQuotes.length > 0 && (
                        <div className="space-y-1">
                            {requiredQuotes.map((quote, index) => (
                                <div key={index} className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2">
                                        <div className={`${getQuoteStatusColor(quote.type)}`}>
                                            {getQuoteIcon(quote.type)}
                                        </div>
                                        <span className="font-medium capitalize">{quote.type}</span>
                                        <div className={`w-2 h-2 rounded-full ${getStatusDot(quote.designStatus || quote.design_status)}`}></div>
                                    </div>
                                    <span className="text-gray-600 truncate max-w-20">
                                        {quote.type === 'timber' ? 'N/A' :
                                            quote.designers && quote.designers.length > 0
                                                ? quote.designers[0].name.split(' ')[0]
                                                : "Unassigned"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {needsEstimation && (
                        <div className="border-t border-gray-100 pt-2">
                            <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <FiDollarSign className={`w-3 h-3 ${getQuoteStatusColor('estimation')}`} />
                                    <span className="font-medium">Estimation</span>
                                    <div className={`w-2 h-2 rounded-full ${getStatusDot(estimationStatus)}`}></div>
                                </div>
                                <span className="text-gray-600 truncate max-w-20">
                                    {assignment.estimator?.name ? assignment.estimator.name.split(' ')[0] : "Unassigned"}
                                </span>
                            </div>
                        </div>
                    )}

                    {requiredQuotes.length === 0 && !needsEstimation && (
                        <p className="text-xs text-gray-500 italic text-center py-1">
                            No quotes required
                        </p>
                    )}

                    <div className="border-t border-gray-100 pt-2 flex items-center gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span>Completed</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span>In Progress</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span>Unassigned</span>
                        </div>
                    </div>
                </div>
            </div>
        );

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className="flex items-center gap-1 flex-shrink-0 cursor-help"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {(assignment.quotes?.roof?.isRequired || assignment.quotes?.roof?.is_required) && (
                        <FiHome
                            className={`w-4 h-4 ${getQuoteStatusColor('roof')}`}
                            title={`Roof - ${assignment.quotes.roof.designStatus || assignment.quotes.roof.design_status}`}
                        />
                    )}

                    {(assignment.quotes?.wall?.isRequired || assignment.quotes?.wall?.is_required) && (
                        <BrickWall
                            className={`w-4 h-4 ${getQuoteStatusColor('wall')}`}
                            title={`Wall - ${assignment.quotes.wall.designStatus || assignment.quotes.wall.design_status}`}
                        />
                    )}

                    {(assignment.quotes?.floor?.isRequired || assignment.quotes?.floor?.is_required) && (
                        <Grid2X2
                            className={`w-4 h-4 ${getQuoteStatusColor('floor')}`}
                            title={`Floor - ${assignment.quotes.floor.designStatus || assignment.quotes.floor.design_status}`}
                        />
                    )}

                    {needsEstimation && (
                        <FiDollarSign
                            className={`w-4 h-4 ${getQuoteStatusColor('estimation')}`}
                            title="Estimation status"
                        />
                    )}
                </div>

                {isHovered && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const SingleQuoteDisplay = ({ assignment, quoteType }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 150;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;
                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getQuoteStatusColor = () => {
            const quote = assignment.quotes?.[quoteType];
            if (!quote?.isRequired && !quote?.is_required) return 'text-gray-300';

            const status = quote.designStatus || quote.design_status;
            switch (status) {
                case 'waiting_assignment': return 'text-red-500';
                case 'in_progress': return 'text-yellow-500';
                case 'completed': return 'text-green-500';
                default: return 'text-gray-400';
            }
        };

        const getQuoteIcon = () => {
            switch (quoteType) {
                case 'roof': return <FiHome className="w-5 h-5" />;
                case 'wall': return <BrickWall className="w-5 h-5" />;
                case 'floor': return <Grid2X2 className="w-5 h-5" />;
                case 'timber': return <GiWoodBeam className="w-5 h-5" />;
                default: return <FiLayers className="w-5 h-5" />;
            }
        };

        const getStatusDot = (status) => {
            switch (status) {
                case 'completed': return 'bg-green-500';
                case 'in_progress': return 'bg-yellow-500';
                case 'waiting_assignment': return 'bg-red-500';
                default: return 'bg-gray-400';
            }
        };

        const quote = assignment._quoteData || assignment.quotes?.[quoteType];

        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 256 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 256 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-xs capitalize">{quoteType} Quote Status</h4>

                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">Status:</span>
                            <div className={`w-2 h-2 rounded-full ${getStatusDot(quote?.designStatus || quote?.design_status)}`}></div>
                        </div>
                        <span className="text-gray-600 capitalize">
                            {(quote?.designStatus || quote?.design_status)?.replace('_', ' ') || 'N/A'}
                        </span>
                    </div>

                    {quote?.designers && quote.designers.length > 0 && (
                        <div className="border-t border-gray-100 pt-2">
                            <span className="text-xs font-medium text-gray-700">Designers:</span>
                            <div className="mt-1 space-y-1">
                                {quote.designers.map((designer, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                        <span className="text-gray-600">{designer.name}</span>
                                        {(designer.isCompleted || designer.is_completed) && (
                                            <FiCheckCircle className="w-3 h-3 text-green-500" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {quoteType === 'timber' ? (
                        <p className="text-xs text-gray-500 italic">Timber jobs don't require designers</p>
                    ) : (
                        (!quote?.designers || quote.designers.length === 0) && (
                            <p className="text-xs text-red-500 italic">No designer assigned</p>
                        )
                    )}
                </div>
            </div>
        );

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className={`cursor-help ${getQuoteStatusColor()}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {getQuoteIcon()}
                </div>
                {isHovered && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    return (
        <div className="">
            <StickyToolbar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedQuoteTypes={selectedQuoteTypes}
                setSelectedQuoteTypes={setSelectedQuoteTypes}
                viewMode={viewMode}
                setViewMode={setViewMode}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                isVisible={showStickyToolbar}
                navbarHeight={navbarHeight}
                expandedAssignments={expandedAssignments}
                filteredAssignments={filteredAssignments}
            />

            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Job Editor</h3>
                <div className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                        {searchQuery
                            ? `${expandedAssignments.length} rows (${filteredAssignments.length} assignments)`
                            : viewMode === 'separated'
                                ? `${expandedAssignments.length} rows (${assignments.length} assignments)`
                                : `${assignments.length} assignments`
                        }
                    </span>
                    <div className="flex items-center gap-2 ml-4">
                        <span className="text-xs text-gray-600">View:</span>
                        <div className="flex bg-gray-200 rounded-md p-0.5">
                            <button
                                onClick={() => setViewMode('separated')}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'separated'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Separated
                            </button>
                            <button
                                onClick={() => setViewMode('combined')}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'combined'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Combined
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={columnToggleRef}>
                <ColumnToggle
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    setColumnSizing={setColumnSizing}
                    selectedQuoteTypes={selectedQuoteTypes}
                    setSelectedQuoteTypes={setSelectedQuoteTypes}
                />
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <table className="min-w-full table-fixed" style={{ width: table.getTotalSize() }}>
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide border-r border-gray-200/70 last:border-r-0 relative"
                                        style={{ width: header.getSize() }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-blue-400 ${header.column.getIsResizing() ? 'bg-blue-500' : ''
                                                }`}
                                            style={{
                                                transform: header.column.getIsResizing() ? 'translateX(0)' : '',
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="max-h-96 overflow-y-auto">
                        {(isTableLoading || isLoading) ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-16 text-center">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                        <span className="text-gray-600 font-medium">Loading assignments...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredAssignments.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-12 text-center">
                                    {searchQuery ? (
                                        <>
                                            <FiGrid className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500 font-medium">No assignments found matching "{searchQuery}"</p>
                                            <p className="text-gray-400 text-sm mt-1">Try adjusting your search terms</p>
                                        </>
                                    ) : (
                                        <>
                                            <FiGrid className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500 font-medium">No assignments found</p>
                                            <p className="text-gray-400 text-sm mt-1">Assignments will appear here when available</p>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ) : (
                            <>
                                {table.getRowModel().rows.map((row, index) => {
                                    const isUrgent = row.original.jobInformation?.urgentQuote ||
                                        row.original.jobInformation?.urgentDesign ||
                                        row.original.urgent_quote ||
                                        row.original.urgent_design;
                                    return (
                                        <AssignmentContextMenu
                                            key={row.id}
                                            assignment={row.original}
                                            onOpenAssignment={handleOpenAssignment}
                                            onEditAssignment={handleEditAssignment}
                                            onAssignDesigner={handleAssignDesigner}
                                            availableDesigners={availableDesigners}
                                            page="editor"
                                        >
                                            <tr
                                                className={`border-b border-gray-100/80 hover:bg-blue-50/50 cursor-pointer transition-all duration-150 text-sm group ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100/50'
                                                    } ${isUrgent ? '!bg-red-50/80 !hover:bg-red-100/60 !border-red-100' : ''}`}
                                                onClick={() => navigate(`/${company}/assignment/${row.original._originalId || row.original._id || row.original.id}`)}
                                                draggable={userType === 'admin' || userType === 'super_admin'}
                                                onDragStart={(e) => {
                                                    if (userType !== 'admin' && userType !== 'super_admin') {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    const originalAssignment = {
                                                        ...row.original,
                                                        _id: row.original._originalId || row.original._id || row.original.id
                                                    };
                                                    e.dataTransfer.setData('application/json', JSON.stringify(originalAssignment));
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.currentTarget.style.opacity = '0.6';
                                                }}
                                                onDragEnd={(e) => {
                                                    e.currentTarget.style.opacity = '1';
                                                }}
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="px-4 py-2 border-r border-gray-200/40 last:border-r-0"
                                                        style={{ width: cell.column.getSize() }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        </AssignmentContextMenu>
                                    );
                                })}
                                {isLoadingMore && <LoadingRow />}
                                {!hasMore && filteredAssignments.length > 0 && !searchQuery && (
                                    <tr>
                                        <td colSpan={columns.length} className="px-4 py-4 text-center text-gray-500 text-sm">
                                            All assignments loaded
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default JobEditorTable;